import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaStar, FaQuoteLeft, FaMapMarkerAlt, FaReply, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { supabase } from '../lib/supabaseClient';
import ReviewModal from './ReviewModal';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(0);

  const totalPages = Math.ceil(reviews.length / 2);

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    if (totalPages <= 1) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }, 7000);
    return () => clearInterval(timer);
  }, [totalPages, currentPage]);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const goTo = useCallback((page) => {
    setDirection(page > currentPage ? 1 : -1);
    setCurrentPage(page);
  }, [currentPage]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  }, [totalPages]);

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrentPage((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FaStar
        key={i}
        size={14}
        className={i < rating ? 'text-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-PE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 150 : -150, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -150 : 150, opacity: 0 }),
  };

  const currentReviews = reviews.slice(currentPage * 2, currentPage * 2 + 2);

  const renderReviewCard = (review, index) => {
    const hasPhotos = review.photo_urls && review.photo_urls.length > 0;
    return (
      <motion.div
        key={review.id}
        initial={{ opacity: 0, x: index === 0 ? -120 : 120 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, delay: index * 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col"
        style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', transition: 'box-shadow 0.3s ease' }}
        whileHover={{ y: -6, boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15)' }}
      >
        {/* Photos on top */}
        {hasPhotos && (
          <div className={`h-48 md:h-56 w-full ${review.photo_urls.length > 1 ? 'flex gap-0.5' : ''}`}>
            {review.photo_urls.length === 1 ? (
              <button
                onClick={() => setSelectedPhoto(review.photo_urls[0])}
                className="w-full h-full overflow-hidden group"
              >
                <img
                  src={review.photo_urls[0]}
                  alt="Foto del huésped"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </button>
            ) : review.photo_urls.length === 2 ? (
              <>
                {review.photo_urls.map((url, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedPhoto(url)}
                    className="flex-1 h-full overflow-hidden group"
                  >
                    <img
                      src={url}
                      alt={`Foto ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </button>
                ))}
              </>
            ) : (
              <>
                <button
                  onClick={() => setSelectedPhoto(review.photo_urls[0])}
                  className="flex-[2] h-full overflow-hidden group"
                >
                  <img
                    src={review.photo_urls[0]}
                    alt="Foto 1"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </button>
                <div className="flex-1 flex flex-col gap-0.5">
                  {review.photo_urls.slice(1, 3).map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedPhoto(url)}
                      className="flex-1 overflow-hidden group"
                    >
                      <img
                        src={url}
                        alt={`Foto ${i + 2}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6 md:p-8 flex flex-col flex-1">
          {/* Author top */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">
                {review.reviewer_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-gray-900 text-sm">{review.reviewer_name}</h4>
                <span className="text-[10px] font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Huésped verificado</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                {review.reviewer_city && (
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <FaMapMarkerAlt size={8} />
                    {review.reviewer_city}
                  </p>
                )}
                <span className="text-gray-300 text-xs">·</span>
                <p className="text-xs text-gray-400">{formatDate(review.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex gap-0.5">
              {renderStars(review.rating)}
            </div>
            <span className="text-sm font-semibold text-gray-700">{review.rating}.0</span>
          </div>

          {/* Comment */}
          <div className="flex-1 mb-4 relative">
            <FaQuoteLeft className="text-green-100 absolute -top-1 -left-1" size={20} />
            <p className="text-gray-600 text-sm md:text-base leading-relaxed pl-7">
              {review.comment}
            </p>
          </div>

          {/* Admin Response */}
          {review.admin_response && (
            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-lg mt-auto">
              <p className="text-xs font-semibold text-green-700 flex items-center gap-1 mb-1">
                <FaReply size={10} />
                Respuesta del hotel
              </p>
              <p className="text-sm text-green-800">{review.admin_response}</p>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <section id="resenas" className="relative py-20 overflow-hidden" style={{ background: '#ffffff' }}>
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 20% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
            'radial-gradient(ellipse at 80% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
            'radial-gradient(ellipse at 50% 80%, rgba(20, 184, 166, 0.2) 0%, transparent 65%)',
            'radial-gradient(ellipse at 20% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        style={{ zIndex: 1 }}
      />
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 70% 30%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
            'radial-gradient(ellipse at 30% 70%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
            'radial-gradient(ellipse at 60% 20%, rgba(196, 173, 121, 0.15) 0%, transparent 55%)',
            'radial-gradient(ellipse at 70% 30%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
          ],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        style={{ zIndex: 1 }}
      />

      <div className="container mx-auto px-4 relative" style={{ zIndex: 2 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-extrabold text-gray-900">
            Lo que Dicen Nuestros Huéspedes
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Experiencias reales de quienes nos visitaron
          </p>
          <div className="mt-4 w-24 h-1 bg-green-600 mx-auto rounded"></div>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Cargando reseñas...</p>
          </div>
        )}

        {/* No Reviews */}
        {!loading && reviews.length === 0 && (
          <div className="text-center py-12">
            <FaQuoteLeft className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500 text-lg">Aún no hay reseñas. ¡Sé el primero en compartir tu experiencia!</p>
          </div>
        )}

        {/* Carousel */}
        {!loading && reviews.length > 0 && (
          <div className="relative mb-14">
            {/* Navigation Arrows */}
            {totalPages > 1 && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-gray-400 hover:text-green-600 hover:shadow-lg transition-all duration-300"
                >
                  <FaChevronLeft size={14} />
                </button>
                <button
                  onClick={goNext}
                  className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-gray-400 hover:text-green-600 hover:shadow-lg transition-all duration-300"
                >
                  <FaChevronRight size={14} />
                </button>
              </>
            )}

            {/* Review Cards */}
            <div className="overflow-hidden px-2">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentPage}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                >
                  {currentReviews.map((review, i) => renderReviewCard(review, i))}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dots */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`rounded-full transition-all duration-300 ${
                      i === currentPage
                        ? 'w-8 h-2.5 bg-green-600'
                        : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center"
        >
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaStar size={16} />
            Dejar mi Reseña
          </button>
        </motion.div>
      </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={fetchReviews}
      />

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <img
            src={selectedPhoto}
            alt="Foto de reseña"
            className="max-w-full max-h-[90vh] rounded-lg object-contain"
          />
        </div>
      )}
    </section>
  );
};

export default Reviews;
